import { useEffect, useState } from 'react';
import { fillWithBots, leaveLobby, setReady, startTournament, subscribeToPlayers } from '../services/firestore';
import { avatarForId } from '../utils/avatar';
import AppHeader from './AppHeader';
import './Lobby.css';

function Lobby({ me, setMe, tournament, connectionError }) {
  const [players, setPlayers] = useState([]);
  const [playersLoaded, setPlayersLoaded] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    return subscribeToPlayers(
      tournament.id,
      (list) => {
        setPlayers(list);
        setPlayersLoaded(true);
      },
      (err) => {
        console.error(err);
        setError('Could not load players. Check your Firebase connection.');
      }
    );
  }, [tournament.id]);

  // If our stored identity no longer matches a real player doc, drop back to
  // the home screen instead of getting stuck on a lobby we're not part of.
  useEffect(() => {
    if (!playersLoaded) return;
    const stillJoined = players.some((player) => player.id === me.id);
    if (!stillJoined) setMe(null);
  }, [players, playersLoaded, me.id, setMe]);

  const isHost = tournament.hostId === me.id;
  const myPlayer = players.find((player) => player.id === me.id);
  const allReady = players.length > 0 && players.every((player) => player.ready);
  const canStart = isHost && players.length >= 2 && allReady;
  const emptySlots = Math.max(0, tournament.maxPlayers - players.length);

  const handleToggleReady = async () => {
    if (!myPlayer) return;
    try {
      await setReady(me.id, !myPlayer.ready);
    } catch (err) {
      console.error(err);
      setError('Could not update ready status.');
    }
  };

  const handleStart = async () => {
    try {
      await startTournament(players, tournament.id);
    } catch (err) {
      console.error(err);
      setError('Could not start the tournament. Please try again.');
    }
  };

  const handleForfeit = () => {
    leaveLobby(me.id, tournament.id).catch(console.error);
    setMe(null);
  };

  const handleFillWithBots = async () => {
    if (emptySlots === 0) return;
    try {
      await fillWithBots(tournament.id, emptySlots);
    } catch (err) {
      console.error(err);
      setError('Could not add bots.');
    }
  };

  const handleCopyLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}?code=${tournament.roomCode}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="page">
      <div className="panel">
        <AppHeader
          subtitle="Tournament Lobby"
          topRight={
            <span className="badge">
              {players.length} / {tournament.maxPlayers} JOINED
            </span>
          }
          middleItems={[
            `Room Code: ${tournament.roomCode}`,
            '⏳ Waiting for players · Not started',
            'Best of 3 · Single elimination',
          ]}
          bottomLeft="Share this room and wait for friends to join"
          bottomRight={<span className="badge-blue">HOST: {tournament.hostName}</span>}
        />

        <div className="lobby-body">
          <div className="lobby-left">
            <div className="code-card">
              <div>
                <div className="code-label">Room Code</div>
                <div className="code-val">{tournament.roomCode}</div>
              </div>
              <button className="btn-copy" onClick={handleCopyLink}>
                {copied ? '✓ Copied!' : '🔗 Copy Invite Link'}
              </button>
            </div>

            <div className="card">
              <h3>Tournament Settings</h3>
              <div className="setting-row">
                <div className="setting-label">Format</div>
                <div className="setting-val">Single Elimination</div>
              </div>
              <div className="setting-row">
                <div className="setting-label">Match Length</div>
                <div className="setting-val">Best of 3</div>
              </div>
              <div className="setting-row">
                <div className="setting-label">Max Players</div>
                <div className="setting-val">{tournament.maxPlayers}</div>
              </div>
              <div className="setting-row">
                <div className="setting-label">Time per Round</div>
                <div className="setting-val">10 seconds</div>
              </div>
              <div className="setting-row">
                <div className="setting-label">Tiebreaker</div>
                <div className="setting-val">Sudden Death</div>
              </div>
            </div>

            {myPlayer && (
              <button className={`btn-ready${myPlayer.ready ? ' is-ready' : ''}`} onClick={handleToggleReady}>
                {myPlayer.ready ? '✓ Ready' : "I'm Ready"}
              </button>
            )}

            {isHost ? (
              <>
                <button className="btn-start" onClick={handleStart} disabled={!canStart}>
                  ⚡ Start Tournament
                </button>
                <div className="host-note">
                  {players.length < 2
                    ? 'Need at least 2 players to start'
                    : !allReady
                    ? 'Waiting for everyone to ready up'
                    : `Only the host can start · ${emptySlots} slots still open`}
                </div>
                <button className="btn-fill-bots" onClick={handleFillWithBots} disabled={emptySlots === 0}>
                  🤖 Fill with Bots
                </button>
              </>
            ) : (
              <div className="host-note">Waiting for {tournament.hostName} to start the tournament...</div>
            )}

            <button className="btn-ghost btn-ghost-danger" onClick={handleForfeit}>
              🏳️ Forfeit Tournament
            </button>
          </div>

          <div className="lobby-right">
            <div className="card players-card">
              <h3>
                Players in Lobby{' '}
                <span className="player-count">
                  {players.length} / {tournament.maxPlayers}
                </span>
              </h3>
              <div className="player-list-scroll">
                {players.map((player, index) => {
                  const avatar = avatarForId(player.id);
                  return (
                    <div className="player-row" key={player.id}>
                      <div className="player-num">{index + 1}</div>
                      <div className="player-emoji">{player.isBot ? '🤖' : avatar.emoji}</div>
                      <div className="player-name">{player.name}</div>
                      {player.isHost && <div className="host-badge">HOST</div>}
                      {player.isBot && <div className="bot-badge">{player.difficulty?.toUpperCase()}</div>}
                      <div className={player.ready ? 'ready-dot' : 'waiting-dot'} />
                    </div>
                  );
                })}
                {Array.from({ length: emptySlots }).map((_, index) => (
                  <div className="empty-slot" key={`empty-${index}`}>
                    <div className="slot-num">{players.length + index + 1}</div>
                    <div className="slot-label">Waiting for player...</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {error && <p className="error">{error}</p>}
        {connectionError && <p className="error">{connectionError}</p>}
      </div>
    </div>
  );
}

export default Lobby;
